import { useEffect } from 'react'
import type { ReactNode } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { ScrollIcon, BookIcon } from '@/components/game/GameIcons'
import { SeoHead } from '@/components/SeoHead'
import { ROUTE_SEO } from '@/seo'

/**
 * /legal — Informations légales : Mentions légales (LCEN), CGU, et Politique de
 * confidentialité (RGPD), en une page à trois sections ancrées.
 *
 * Page PUBLIQUE, accessible hors connexion (route hors ProtectedRoute) : la loi
 * impose que ces mentions soient consultables par tout visiteur. Le pied de page
 * (AppLayout) expose les trois liens vers les ancres #mentions / #cgu / #confidentialite.
 *
 * ⚠️ Ce ne sont pas un conseil juridique. Données d'éditeur/hébergeur/contact
 * renseignées ci-dessous ; à faire relire par un pro avant d'ajouter des
 * paiements ou de passer à l'échelle.
 */

const CONTACT_EMAIL = 'contact@corsalia.net'
const LAST_UPDATED = '27 juin 2026'

export function Legal() {
  const { hash } = useLocation()

  // Footer links land here with a hash (#mentions / #cgu / #confidentialite).
  // React Router doesn't scroll to hashes on its own, so do it here; with no
  // hash, start at the top (the previous page's scroll position would carry over).
  useEffect(() => {
    const root = document.documentElement
    const prev = root.style.scrollBehavior
    root.style.scrollBehavior = 'auto'
    const id = hash.replace('#', '')
    const target = id ? document.getElementById(id) : null
    if (target) {
      target.scrollIntoView({ block: 'start' })
    } else {
      window.scrollTo(0, 0)
    }
    root.style.scrollBehavior = prev
  }, [hash])

  return (
    <div className="relative">
      <SeoHead {...ROUTE_SEO['/legal']} />
      <style>{'@media (prefers-reduced-motion: no-preference){html{scroll-behavior:smooth}}'}</style>

      <div className="max-w-3xl mx-auto px-4 sm:px-6 pt-8 pb-32">
        {/* ---- hero ---- */}
        <header className="text-center">
          <span className="inline-grid place-items-center w-12 h-12 rounded-full bg-ocean-900/60 border border-sand-500/30">
            <ScrollIcon className="w-6 h-6 text-sand-300" />
          </span>
          <h1 className="mt-4 font-display text-3xl sm:text-4xl text-sand-300">Informations légales</h1>
          <p className="mt-3 text-sm text-ocean-100/80">
            Mentions légales, conditions d'utilisation et politique de confidentialité de Corsalia.
          </p>
          <p className="mt-1 text-xs text-ocean-300">Dernière mise à jour : {LAST_UPDATED}</p>
        </header>

        {/* ---- quick nav ---- */}
        <nav className="mt-8 flex flex-wrap justify-center gap-2.5">
          {[
            { to: '#mentions', label: 'Mentions légales' },
            { to: '#cgu', label: "Conditions d'utilisation" },
            { to: '#confidentialite', label: 'Confidentialité (RGPD)' },
          ].map((l) => (
            <a
              key={l.to}
              href={l.to}
              className="rounded-full border border-sand-500/30 bg-ocean-900/40 px-4 py-1.5 text-sm text-ocean-100 hover:border-sand-300/60 hover:text-sand-100 transition-colors"
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="mt-12 space-y-14">
          <MentionsLegales />
          <Cgu />
          <Confidentialite />
        </div>

        {/* ---- back to codex / play ---- */}
        <div className="mt-16 flex flex-col items-center gap-3 text-center">
          <p className="text-sm text-ocean-300">Les règles du jeu, elles, sont dans le grand livre.</p>
          <Link
            to="/codex"
            className="inline-flex items-center gap-2 rounded-lg border border-sand-500/30 bg-ocean-900/40 px-5 py-2.5 text-sm text-ocean-100 hover:border-sand-300/60 hover:text-sand-100 transition-colors"
          >
            <BookIcon className="w-4 h-4" />
            Consulter le Codex
          </Link>
        </div>
      </div>
    </div>
  )
}

/* ─────────────────────────── 1. Mentions légales ─────────────────────────── */
function MentionsLegales() {
  return (
    <Section id="mentions" title="Mentions légales" note="Obligatoires au titre de la loi LCEN du 21 juin 2004.">
      <H3>Éditeur du site et du jeu</H3>
      <P>
        Corsalia est édité par <strong className="text-sand-100">Foucault Jérémy</strong>, particulier, domicilié 7 Allée
        des Rouges Gorges, 33160 Saint Aubin de Médoc, France. Directeur de la publication : Jérémy Foucault. Contact :{' '}
        <Mail />.
      </P>

      <H3>Hébergement</H3>
      <P>
        Le site est hébergé par <strong className="text-sand-100">Hetzner Online GmbH</strong>, Industriestr. 25, 91710
        Gunzenhausen, Allemagne :{' '}
        <ExtLink href="https://www.hetzner.com">www.hetzner.com</ExtLink>.
      </P>

      <H3>Propriété intellectuelle</H3>
      <P>
        L'ensemble des éléments du jeu Corsalia (code, textes, interface, graphismes, identité visuelle, nom et logo) est
        protégé par le droit de la propriété intellectuelle et demeure la propriété exclusive de l'éditeur, sauf mention
        contraire.
      </P>
    </Section>
  )
}

/* ──────────────────────── 2. Conditions d'utilisation ─────────────────────── */
function Cgu() {
  return (
    <Section id="cgu" title="Conditions Générales d'Utilisation (CGU)">
      <Article n={1} title="Objet">
        Les présentes CGU régissent l'accès et l'utilisation du jeu Corsalia (le « Jeu »), jeu de stratégie maritime
        jouable dans le navigateur, accessible à l'adresse <ExtLink href="https://corsalia.net">corsalia.net</ExtLink>.
        En créant un compte ou en utilisant le Jeu, l'utilisateur (le « Joueur ») accepte sans réserve les présentes CGU.
      </Article>

      <Article n={2} title="Phase bêta et disponibilité du service">
        Le Jeu est actuellement proposé en <strong className="text-sand-100">phase bêta (version d'essai)</strong>. Le
        Joueur reconnaît et accepte que :
        <ul className="mt-2 space-y-1.5 list-disc pl-5 marker:text-sand-500/70">
          <LI>le Jeu est fourni « en l'état », et peut contenir des bugs, des erreurs ou des fonctionnalités incomplètes ;</LI>
          <LI>
            l'éditeur peut <strong className="text-sand-100">modifier, suspendre, interrompre ou fermer</strong> tout ou
            partie du Jeu à tout moment, sans préavis et sans indemnité ;
          </LI>
          <LI>
            les données de jeu (progression, ressources, flotte, classements){' '}
            <strong className="text-sand-100">peuvent être réinitialisées, modifiées ou supprimées</strong>, notamment
            lors des remises à zéro saisonnières ou des phases de test, sans ouvrir droit à compensation ;
          </LI>
          <LI>l'éditeur ne garantit ni la disponibilité continue, ni la conservation des données du Jeu.</LI>
        </ul>
      </Article>

      <Article n={3} title="Accès et compte">
        L'accès au Jeu nécessite la création d'un compte (e-mail, pseudonyme, mot de passe). Le Joueur s'engage à fournir
        des informations exactes, à conserver ses identifiants confidentiels, et demeure responsable de toute activité
        réalisée depuis son compte. Le compte est strictement personnel. En phase bêta, l'absence de procédure de
        récupération du mot de passe implique que le Joueur conserve soigneusement ses identifiants.
      </Article>

      <Article n={4} title="Âge minimum">
        Le Jeu est réservé aux personnes âgées d'au moins <strong className="text-sand-100">15 ans</strong>. Un mineur de
        moins de 15 ans ne peut créer de compte qu'avec l'accord préalable de son représentant légal.
      </Article>

      <Article n={5} title="Règles de conduite">
        Le Joueur s'engage à ne pas :
        <ul className="mt-2 space-y-1.5 list-disc pl-5 marker:text-sand-500/70">
          <LI>tricher, utiliser des robots, automatiser le jeu, ou exploiter sciemment un bug ou une faille ;</LI>
          <LI>perturber le fonctionnement du Jeu ou des serveurs, ni tenter d'y accéder de façon non autorisée ;</LI>
          <LI>harceler, menacer ou nuire à d'autres joueurs, ni contourner les protections anti-grief ;</LI>
          <LI>
            utiliser un pseudonyme ou diffuser un contenu illégal, offensant, diffamatoire ou portant atteinte aux droits
            de tiers.
          </LI>
        </ul>
        <span className="mt-2 block">Tout manquement peut entraîner la suspension ou la suppression du compte (article 8).</span>
      </Article>

      <Article n={6} title="Caractère gratuit et évolutions">
        Le Jeu est actuellement <strong className="text-sand-100">gratuit</strong>. L'éditeur se réserve la possibilité
        d'introduire ultérieurement des options payantes ; conformément à sa politique, celles-ci porteront sur le confort
        et l'esthétique et n'offriront aucun avantage de puissance, de butin ou de vitesse (principe « anti-pay-to-win »).
        Toute offre payante fera l'objet d'une mise à jour des présentes CGU et des informations précontractuelles requises
        par le droit de la consommation.
      </Article>

      <Article n={7} title="Propriété intellectuelle et contenus du Joueur">
        Le Jeu et l'ensemble de ses éléments restent la propriété de l'éditeur, qui concède au Joueur un droit d'usage
        personnel, non exclusif et non transférable, limité à la durée d'utilisation du Jeu. Le Joueur reste responsable
        des contenus qu'il transmet (pseudonyme, signalements, suggestions) et garantit qu'ils ne portent pas atteinte aux
        droits de tiers. Le Joueur autorise l'éditeur à utiliser librement et sans contrepartie les retours, suggestions et
        signalements transmis, afin d'améliorer le Jeu.
      </Article>

      <Article n={8} title="Suspension et résiliation">
        L'éditeur peut suspendre ou supprimer, sans préavis, tout compte en cas de manquement aux présentes CGU. Le Joueur
        peut à tout moment cesser d'utiliser le Jeu et demander la suppression de son compte (voir la Politique de
        confidentialité).
      </Article>

      <Article n={9} title="Responsabilité">
        Le Jeu étant fourni gratuitement et en l'état, l'éditeur ne saurait être tenu responsable, dans les limites
        permises par la loi, des dommages indirects, des pertes de données, des interruptions ou indisponibilités du
        service. L'éditeur met en œuvre des moyens raisonnables pour assurer le bon fonctionnement et la sécurité du Jeu,
        sans obligation de résultat. Rien dans les présentes n'exclut la responsabilité de l'éditeur lorsque la loi
        l'interdit.
      </Article>

      <Article n={10} title="Force majeure">
        La responsabilité de l'éditeur ne saurait être engagée en cas d'inexécution due à un événement de force majeure au
        sens du droit français.
      </Article>

      <Article n={11} title="Modification des CGU">
        L'éditeur peut modifier les présentes CGU. Les modifications substantielles seront portées à la connaissance des
        Joueurs par un moyen approprié. La poursuite de l'utilisation du Jeu après mise à jour vaut acceptation des
        nouvelles CGU.
      </Article>

      <Article n={12} title="Droit applicable et litiges">
        Les présentes CGU sont soumises au <strong className="text-sand-100">droit français</strong>. En cas de litige,
        une solution amiable sera recherchée en priorité. À défaut d'accord, les tribunaux français seront compétents,
        dans le respect des règles protectrices applicables aux consommateurs.
      </Article>

      <Article n={13} title="Contact">
        Pour toute question relative aux présentes CGU : <Mail />.
      </Article>
    </Section>
  )
}

/* ─────────────────────── 3. Politique de confidentialité ──────────────────── */
function Confidentialite() {
  return (
    <Section
      id="confidentialite"
      title="Politique de confidentialité (RGPD)"
      note="Conforme au Règlement (UE) 2016/679 (« RGPD ») et à la loi « Informatique et Libertés »."
    >
      <H3>1. Responsable du traitement</H3>
      <P>
        Le responsable du traitement est <strong className="text-sand-100">Foucault Jérémy</strong>, particulier,
        domicilié 7 Allée des Rouges Gorges, 33160 Saint Aubin de Médoc, France. Contact : <Mail />.
      </P>
      <P className="text-ocean-300">
        Compte tenu de la taille du projet et de l'absence de traitement à grande échelle, aucun délégué à la protection
        des données (DPO) n'est désigné ; l'éditeur est votre point de contact.
      </P>

      <H3>2. Données collectées</H3>
      <ul className="mt-2 space-y-1.5 list-disc pl-5 marker:text-sand-500/70">
        <LI>
          <strong className="text-sand-100">Données de compte</strong> : adresse e-mail, pseudonyme, mot de passe (stocké
          sous forme hachée).
        </LI>
        <LI>
          <strong className="text-sand-100">Données techniques et de connexion</strong> : adresse IP, type de navigateur
          / d'appareil, journaux de connexion, dates et heures d'activité.
        </LI>
        <LI>
          <strong className="text-sand-100">Données de jeu</strong> : progression, ressources, flotte, actions, scores,
          dernière connexion.
        </LI>
        <LI>
          <strong className="text-sand-100">Communications</strong> : contenu des bugs ou suggestions transmis via le
          bouton prévu dans le Jeu, et les informations techniques jointes automatiquement (page concernée, pseudonyme,
          navigateur).
        </LI>
      </ul>
      <P>
        Le Jeu ne collecte aucune donnée dite « sensible » et ne réalise aucune décision entièrement automatisée
        produisant des effets juridiques à votre égard.
      </P>

      <H3>3. Finalités et bases légales</H3>
      <div className="mt-3 overflow-x-auto">
        <table className="w-full text-sm border-collapse">
          <thead>
            <tr className="text-left text-sand-300">
              <th className="border-b border-sand-500/25 py-2 pr-4 font-semibold">Finalité</th>
              <th className="border-b border-sand-500/25 py-2 font-semibold">Base légale</th>
            </tr>
          </thead>
          <tbody className="text-ocean-100/90">
            <tr>
              <td className="border-b border-sand-500/10 py-2 pr-4">Création et gestion du compte, fourniture du Jeu</td>
              <td className="border-b border-sand-500/10 py-2">Exécution du contrat (CGU), art. 6.1.b</td>
            </tr>
            <tr>
              <td className="border-b border-sand-500/10 py-2 pr-4">
                Sécurité, prévention de la triche, des abus et du grief, fonctionnement technique, débogage
              </td>
              <td className="border-b border-sand-500/10 py-2">Intérêt légitime, art. 6.1.f</td>
            </tr>
            <tr>
              <td className="py-2 pr-4">Traitement des bugs / suggestions que vous envoyez</td>
              <td className="py-2">Intérêt légitime (amélioration du Jeu), art. 6.1.f</td>
            </tr>
          </tbody>
        </table>
      </div>

      <H3>4. Destinataires et sous-traitants</H3>
      <P>Vos données sont accessibles à l'éditeur et à ses sous-traitants techniques, uniquement dans la mesure nécessaire :</P>
      <ul className="mt-2 space-y-1.5 list-disc pl-5 marker:text-sand-500/70">
        <LI>
          <strong className="text-sand-100">Hébergement</strong> : Hetzner Online GmbH (Allemagne) ;
        </LI>
        <LI>
          <strong className="text-sand-100">Discord</strong> : réception des signalements de bugs et suggestions via
          webhook.
        </LI>
      </ul>
      <P>Vos données ne sont ni vendues, ni louées à des tiers.</P>

      <H3>5. Transferts hors Union européenne</H3>
      <P>
        L'hébergement est assuré au sein de l'Union européenne (Allemagne). Discord, en revanche, est susceptible de
        traiter certaines données (contenu d'un signalement, pseudonyme) en dehors de l'Union européenne, notamment aux
        États-Unis. Ces transferts sont encadrés par des garanties appropriées : clauses contractuelles types de la
        Commission européenne et/ou adhésion au cadre de protection des données UE–États-Unis.
      </P>

      <H3>6. Durées de conservation</H3>
      <ul className="mt-2 space-y-1.5 list-disc pl-5 marker:text-sand-500/70">
        <LI>
          <strong className="text-sand-100">Compte</strong> : conservé tant qu'il est actif, puis supprimé dans un délai
          de 12 mois après suppression du compte ou inactivité prolongée.
        </LI>
        <LI>
          <strong className="text-sand-100">Journaux techniques</strong> : 12 mois maximum.
        </LI>
        <LI>
          <strong className="text-sand-100">Signalements / suggestions</strong> : le temps nécessaire à leur traitement,
          puis archivage limité.
        </LI>
      </ul>
      <P>Au-delà, les données sont supprimées ou anonymisées.</P>

      <H3>7. Vos droits</H3>
      <P>
        Conformément au RGPD, vous disposez des droits d'<strong className="text-sand-100">accès, de rectification,
        d'effacement, de limitation, d'opposition, de portabilité</strong>, ainsi que du droit de{' '}
        <strong className="text-sand-100">retirer votre consentement</strong> à tout moment. Pour les exercer, écrivez à{' '}
        <Mail /> ; nous répondons dans un délai d'un mois.
      </P>
      <P>
        Vous pouvez également introduire une réclamation auprès de la <strong className="text-sand-100">CNIL</strong>{' '}
        (Commission Nationale de l'Informatique et des Libertés, 3 Place de Fontenoy, TSA 80715, 75334 Paris Cedex 07 :{' '}
        <ExtLink href="https://www.cnil.fr">www.cnil.fr</ExtLink>).
      </P>

      <H3>8. Cookies et stockage local</H3>
      <P>
        Le Jeu n'utilise aucun cookie publicitaire ni outil de mesure d'audience. L'authentification repose sur un jeton
        stocké dans le stockage local (<code className="text-sand-100">localStorage</code>) de votre navigateur,
        strictement nécessaire au fonctionnement du Jeu et qui ne requiert pas votre consentement. Si un traceur non
        essentiel était ajouté à l'avenir, un bandeau de consentement serait mis en place et votre accord préalable
        recueilli.
      </P>

      <H3>9. Sécurité</H3>
      <P>
        L'éditeur met en œuvre des mesures raisonnables pour protéger vos données : chiffrement des échanges (HTTPS),
        stockage haché des mots de passe, contrôle des accès. Aucun système n'étant infaillible, une sécurité absolue ne
        peut être garantie.
      </P>

      <H3>10. Mineurs</H3>
      <P>
        Le Jeu est réservé aux personnes d'au moins 15 ans. Si nous apprenons qu'un compte a été créé par un mineur de
        moins de 15 ans sans l'accord de son représentant légal, les données associées seront supprimées.
      </P>

      <H3>11. Modifications</H3>
      <P>
        La présente politique peut évoluer. Toute modification substantielle sera signalée par un moyen approprié et la
        date de mise à jour ci-dessus sera actualisée.
      </P>

      <H3>12. Contact</H3>
      <P>
        Pour toute question relative à vos données : <Mail />.
      </P>
    </Section>
  )
}

/* ─────────────────────────── presentational bits ─────────────────────────── */
function Section({ id, title, note, children }: { id: string; title: string; note?: string; children: ReactNode }) {
  return (
    <section id={id} className="scroll-mt-24">
      <h2 className="font-display text-2xl sm:text-3xl text-sand-300 border-b border-sand-500/20 pb-3">{title}</h2>
      {note && <p className="mt-2 text-xs italic text-ocean-300">{note}</p>}
      <div className="mt-5 space-y-4 text-sm sm:text-[0.95rem] leading-relaxed text-ocean-100/90">{children}</div>
    </section>
  )
}

function Article({ n, title, children }: { n: number; title: string; children: ReactNode }) {
  return (
    <div>
      <H3>
        Article {n} : {title}
      </H3>
      <div className="space-y-2">{typeof children === 'string' ? <P>{children}</P> : children}</div>
    </div>
  )
}

function H3({ children }: { children: ReactNode }) {
  return <h3 className="font-display text-base sm:text-lg text-sand-300 mb-1.5">{children}</h3>
}

function P({ children, className = '' }: { children: ReactNode; className?: string }) {
  return <p className={className}>{children}</p>
}

function LI({ children }: { children: ReactNode }) {
  return <li>{children}</li>
}

function Mail() {
  return (
    <a href={`mailto:${CONTACT_EMAIL}`} className="text-sand-300 hover:text-sand-100 underline underline-offset-2">
      {CONTACT_EMAIL}
    </a>
  )
}

function ExtLink({ href, children }: { href: string; children: ReactNode }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noreferrer noopener"
      className="text-sand-300 hover:text-sand-100 underline underline-offset-2"
    >
      {children}
    </a>
  )
}
